import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Users,
  DollarSign,
  Target,
  Clock,
  UserMinus,
  ArrowRight,
  Check
} from 'lucide-react'
import { clsx } from 'clsx'
import type { ChangeAlert, ChangeAlertType, ChangeAlertSeverity } from '@/types'

interface ChangeAlertPanelProps {
  alerts: ChangeAlert[]
  onDismiss?: (alertId: string, reason?: string) => Promise<void>
  onViewDetail?: (alert: ChangeAlert) => void
  onRefresh?: () => void
  isLoading?: boolean
  compact?: boolean
}

// 图标映射
const ALERT_ICONS: Record<ChangeAlertType, React.ComponentType<{ className?: string }>> = {
  stance_flip: RefreshCw,
  budget_change: DollarSign,
  competitor_entry: Target,
  key_person_change: Users,
  window_change: Clock,
  contact_silent: UserMinus,
}

// 严重程度样式
const SEVERITY_STYLES: Record<ChangeAlertSeverity, {
  bg: string
  border: string
  icon: string
  text: string
  badge: string
}> = {
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    text: 'text-red-800',
    badge: 'bg-red-100 text-red-700',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-500',
    text: 'text-amber-800',
    badge: 'bg-amber-100 text-amber-700',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    text: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-700',
  },
}

// 类型显示名称
const TYPE_LABELS: Record<ChangeAlertType, string> = {
  stance_flip: '立场翻转',
  budget_change: '预算变化',
  competitor_entry: '竞品入局',
  key_person_change: '关键人变化',
  window_change: '窗口期变化',
  contact_silent: '联系人沉默',
}

/**
 * 变更预警面板组件
 * 
 * 展示 Deal 的变更预警列表，支持：
 * - 按严重程度分组显示
 * - 展开/收起详情
 * - 忽略预警
 * - 查看推荐动作
 */
export default function ChangeAlertPanel({
  alerts,
  onDismiss,
  onViewDetail,
  onRefresh,
  isLoading = false,
  compact = false,
}: ChangeAlertPanelProps) {
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null)
  const [dismissingId, setDismissingId] = useState<string | null>(null)

  // 按严重程度分组
  const alertsByLevel = {
    error: alerts.filter(a => a.severity === 'error' && !a.dismissed),
    warning: alerts.filter(a => a.severity === 'warning' && !a.dismissed),
    info: alerts.filter(a => a.severity === 'info' && !a.dismissed),
  }

  const activeAlerts = [...alertsByLevel.error, ...alertsByLevel.warning, ...alertsByLevel.info]
  const hasAlerts = activeAlerts.length > 0

  const handleDismiss = async (alertId: string) => {
    if (!onDismiss) return
    setDismissingId(alertId)
    try {
      await onDismiss(alertId)
    } finally {
      setDismissingId(null)
    }
  }

  if (!hasAlerts && !isLoading) {
    return null
  }

  if (compact) {
    // 紧凑模式：只显示数量徽章
    return (
      <div className="flex items-center gap-2">
        {alertsByLevel.error.length > 0 && (
          <span className={clsx(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            SEVERITY_STYLES.error.badge
          )}>
            <AlertCircle className="w-3 h-3" />
            {alertsByLevel.error.length}
          </span>
        )}
        {alertsByLevel.warning.length > 0 && (
          <span className={clsx(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            SEVERITY_STYLES.warning.badge
          )}>
            <AlertTriangle className="w-3 h-3" />
            {alertsByLevel.warning.length}
          </span>
        )}
        {alertsByLevel.info.length > 0 && (
          <span className={clsx(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            SEVERITY_STYLES.info.badge
          )}>
            <Info className="w-3 h-3" />
            {alertsByLevel.info.length}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          变更预警
          {hasAlerts && (
            <span className="text-xs font-normal text-gray-500">
              ({activeAlerts.length} 条待处理)
            </span>
          )}
        </h3>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
          </button>
        )}
      </div>

      {/* 预警列表 */}
      <AnimatePresence mode="popLayout">
        {activeAlerts.map((alert) => {
          const styles = SEVERITY_STYLES[alert.severity]
          const Icon = ALERT_ICONS[alert.type] || AlertTriangle
          const isExpanded = expandedAlertId === alert.id
          const isDismissing = dismissingId === alert.id

          return (
            <motion.div
              key={alert.id}
              layout
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={clsx(
                'rounded-lg border overflow-hidden transition-all',
                styles.bg,
                styles.border
              )}
            >
              {/* 主内容 */}
              <div className="p-3">
                <div className="flex items-start gap-3">
                  {/* 图标 */}
                  <div className={clsx('p-1.5 rounded-lg', styles.bg)}>
                    <Icon className={clsx('w-4 h-4', styles.icon)} />
                  </div>

                  {/* 内容 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={clsx(
                        'text-xs px-1.5 py-0.5 rounded font-medium',
                        styles.badge
                      )}>
                        {TYPE_LABELS[alert.type]}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(alert.createdAt).toLocaleDateString('zh-CN')}
                      </span>
                    </div>
                    <h4 className={clsx('font-medium text-sm', styles.text)}>
                      {alert.title}
                    </h4>
                    {alert.description && !isExpanded && (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                        {alert.description}
                      </p>
                    )}
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setExpandedAlertId(isExpanded ? null : alert.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    {onDismiss && (
                      <button
                        onClick={() => handleDismiss(alert.id)}
                        disabled={isDismissing}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="忽略此预警"
                      >
                        {isDismissing ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 展开详情 */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-gray-200/50"
                  >
                    <div className="p-3 bg-white/50 space-y-3">
                      {/* 详细描述 */}
                      {alert.description && (
                        <p className="text-sm text-gray-600">
                          {alert.description}
                        </p>
                      )}

                      {/* 变更详情 */}
                      {alert.changeDetail && (
                        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(alert.changeDetail, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* 推荐动作 */}
                      {alert.recommendedAction && (
                        <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
                          <ArrowRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-xs font-medium text-blue-700">推荐动作</span>
                            <p className="text-sm text-blue-800 mt-0.5">
                              {alert.recommendedAction}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* 操作按钮 */}
                      <div className="flex items-center justify-end gap-2 pt-2">
                        {onViewDetail && (
                          <button
                            onClick={() => onViewDetail(alert)}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            查看详情
                          </button>
                        )}
                        {onDismiss && (
                          <button
                            onClick={() => handleDismiss(alert.id)}
                            disabled={isDismissing}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <Check className="w-3 h-3" />
                            标记已处理
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </AnimatePresence>

      {/* 空状态 */}
      {!hasAlerts && !isLoading && (
        <div className="text-center py-6 text-gray-400">
          <Check className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">暂无变更预警</p>
        </div>
      )}

      {/* 加载状态 */}
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      )}
    </div>
  )
}

/**
 * 变更预警摘要徽章
 */
export function ChangeAlertBadge({
  alerts,
  onClick,
}: {
  alerts: ChangeAlert[]
  onClick?: () => void
}) {
  const activeAlerts = alerts.filter(a => !a.dismissed)
  const errorCount = activeAlerts.filter(a => a.severity === 'error').length
  const warningCount = activeAlerts.filter(a => a.severity === 'warning').length

  if (activeAlerts.length === 0) {
    return null
  }

  return (
    <button
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
        errorCount > 0
          ? 'bg-red-100 text-red-700 hover:bg-red-200'
          : warningCount > 0
          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
      )}
    >
      <AlertTriangle className="w-3.5 h-3.5" />
      {activeAlerts.length} 条预警
    </button>
  )
}
